import React from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { Download, ArrowRight, Mail, Phone, MapPin, Github, Linkedin } from 'lucide-react';
import aboutImage from '../images/about.png';

const Home = () => {
  const [ref, inView] = useInView({ triggerOnce: true, threshold: 0.1 });

  const downloadCV = () => {
    try {
      // Créer un lien temporaire pour télécharger le CV
      const link = document.createElement('a');
      link.href = '/cv.pdf'; // Chemin vers votre CV dans le dossier public
      link.download = 'CV_Hamza_Zawak.pdf'; // Nom du fichier lors du téléchargement
      link.target = '_blank';
      
      // Ajouter le lien au DOM, cliquer dessus, puis le supprimer
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      // Feedback utilisateur (optionnel - vous pouvez ajouter une notification)
      console.log('CV téléchargé avec succès !');
    } catch (error) {
      console.error('Erreur lors du téléchargement du CV:', error);
      // Fallback : ouvrir dans un nouvel onglet si le téléchargement échoue
      window.open('/cv.pdf', '_blank');
    }
  };

  return (
    <section id="home" className="relative min-h-screen flex items-center justify-center overflow-hidden bg-gradient-to-br from-gray-50 via-white to-gray-100 dark:from-gray-900 dark:via-dark-800 dark:to-gray-900">
      {/* Animated Background Elements */}
      <div className="absolute inset-0 overflow-hidden">
        {/* Programming code rain */}
        <div className="absolute top-0 left-0 w-full h-full opacity-20 dark:opacity-8">
          {[...Array(25)].map((_, i) => (
            <motion.div
              key={i}
              className="absolute text-blue-600 dark:text-blue-400 text-xs font-mono whitespace-nowrap"
              style={{
                left: `${Math.random() * 100}%`,
                animationDelay: `${Math.random() * 5}s`,
                animationDuration: `${15 + Math.random() * 10}s`
              }}
              animate={{
                y: [0, window.innerHeight],
                opacity: [0, 0.6, 0.6, 0]
              }}
              transition={{
                duration: 15 + Math.random() * 10,
                repeat: Infinity,
                ease: "linear"
              }}
            >
              {['const', 'function', 'return', 'if', 'else', 'for', 'while', 'class', 'import', 'export', 'React', 'useState', 'useEffect', 'props', 'state', 'component'].map(char => (
                <span key={char} className="mr-3">{char}</span>
              ))}
            </motion.div>
          ))}
        </div>

        {/* Floating code snippets */}
        <div className="absolute inset-0">
          {[...Array(10)].map((_, i) => (
            <motion.div
              key={i}
              className="absolute text-gray-600 dark:text-gray-400 text-xs font-mono opacity-25 dark:opacity-12"
              style={{
                left: `${Math.random() * 100}%`,
                top: `${Math.random() * 100}%`,
              }}
              animate={{
                y: [0, -40, 0],
                x: [0, 20, 0],
                opacity: [0.15, 0.35, 0.15]
              }}
              transition={{
                duration: 12 + Math.random() * 6,
                repeat: Infinity,
                ease: "easeInOut"
              }}
            >
              <div className="bg-white/80 dark:bg-dark-700/60 p-3 rounded-lg border border-gray-300/70 dark:border-gray-600/50 backdrop-blur-sm shadow-sm">
                <div className="text-blue-600 dark:text-blue-400">function createApp() {'{'}</div>
                <div className="ml-4 text-green-600 dark:text-green-400">return &lt;App /&gt;;</div>
                <div className="text-blue-600 dark:text-blue-400">{'}'}</div>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Additional floating elements */}
        <div className="absolute inset-0">
          {[...Array(8)].map((_, i) => (
            <motion.div
              key={`element-${i}`}
              className="absolute w-2 h-2 bg-blue-500/50 dark:bg-blue-400/30 rounded-full"
              style={{
                left: `${Math.random() * 100}%`,
                top: `${Math.random() * 100}%`,
              }}
              animate={{
                y: [0, -60, 0],
                x: [0, 30, 0],
                scale: [1, 1.5, 1],
                opacity: [0.5, 0.9, 0.5]
              }}
              transition={{
                duration: 8 + Math.random() * 4,
                repeat: Infinity,
                ease: "easeInOut",
                delay: Math.random() * 2
              }}
            />
          ))}
        </div>

        {/* Subtle gradient waves */}
        <div className="absolute inset-0">
          <motion.div
            className="absolute inset-0 bg-gradient-to-r from-transparent via-blue-500/10 dark:via-blue-400/5 to-transparent"
            animate={{
              x: [-100, 100],
              opacity: [0, 0.15, 0]
            }}
            transition={{
              duration: 8,
              repeat: Infinity,
              repeatDelay: 8,
              ease: "easeInOut"
            }}
          />
        </div>

        {/* Gentle scanning line */}
        <motion.div
          className="absolute left-0 w-full h-px bg-gradient-to-r from-transparent via-blue-500/50 dark:via-blue-400/30 to-transparent"
          animate={{
            y: [0, window.innerHeight]
          }}
          transition={{
            duration: 12,
            repeat: Infinity,
            ease: "linear"
          }}
        />

        {/* Subtle geometric shapes */}
        <div className="absolute inset-0">
          {[...Array(6)].map((_, i) => (
            <motion.div
              key={`shape-${i}`}
              className="absolute border border-blue-300/40 dark:border-blue-500/20 rounded-lg"
              style={{
                left: `${Math.random() * 100}%`,
                top: `${Math.random() * 100}%`,
                width: `${20 + Math.random() * 40}px`,
                height: `${20 + Math.random() * 40}px`,
              }}
              animate={{
                rotate: [0, 360],
                scale: [1, 1.2, 1],
                opacity: [0.2, 0.5, 0.2]
              }}
              transition={{
                duration: 20 + Math.random() * 10,
                repeat: Infinity,
                ease: "linear"
              }}
            />
          ))}
        </div>
      </div>

      {/* Main Content */}
      <div className="container-custom relative z-10">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          {/* Text Content */}
          <motion.div
            ref={ref}
            initial={{ opacity: 0, x: -50 }}
            animate={inView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.8 }}
            className="text-center lg:text-left"
          >
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="mb-6"
            >
              <span className="inline-block px-4 py-2 bg-primary-100 dark:bg-primary-900 text-primary-800 dark:text-primary-200 rounded-full text-sm font-medium mb-4">
                👋 Salut, je suis Hamza
              </span>
            </motion.div>

            <motion.h1
              initial={{ opacity: 0, y: 30 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.8, delay: 0.4 }}
              className="text-4xl md:text-6xl font-bold text-gray-900 dark:text-white mb-6"
            >
              Développeur{' '}
              <span className="gradient-text">Full Stack</span>
            </motion.h1>

            <motion.p
              initial={{ opacity: 0, y: 30 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.8, delay: 0.6 }}
              className="text-xl text-gray-700 dark:text-gray-300 mb-8 max-w-2xl mx-auto lg:mx-0"
            >
              Passionné par la création d'applications web modernes et innovantes. 
              Spécialisé en React.js, Node.js et Laravel pour des solutions performantes.
            </motion.p>

            {/* Contact Info */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.8, delay: 0.8 }}
              className="flex flex-wrap justify-center lg:justify-start gap-4 mb-8"
            >
              <div className="flex items-center text-gray-700 dark:text-gray-300 hover:text-primary-600 dark:hover:text-primary-400 transition-colors">
                <Mail size={18} className="mr-2" />
                <span className="text-sm">ezzouekhamza2411@gmail.com</span>
              </div>
              <div className="flex items-center text-gray-700 dark:text-gray-300 hover:text-primary-600 dark:hover:text-primary-400 transition-colors">
                <Phone size={18} className="mr-2" />
                <span className="text-sm">+212 6 40 34 70 45</span>
              </div>
              <div className="flex items-center text-gray-700 dark:text-gray-300 hover:text-primary-600 dark:hover:text-primary-400 transition-colors">
                <MapPin size={18} className="mr-2" />
                <span className="text-sm">Casablanca, Maroc</span>
              </div>
            </motion.div>

            {/* CTA Buttons */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.8, delay: 1 }}
              className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start"
            >
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="btn-primary"
                onClick={() => document.getElementById('about').scrollIntoView({ behavior: 'smooth' })}
              >
                <ArrowRight size={20} className="mr-2" />
                En savoir plus
              </motion.button>
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="btn-secondary"
                onClick={downloadCV}
              >
                <Download size={20} className="mr-2" />
                Télécharger le CV
              </motion.button>
            </motion.div>

            {/* Social Links */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.8, delay: 1.2 }}
              className="flex justify-center lg:justify-start gap-4 mt-8"
            >
              <motion.a
                href="https://github.com/HAMZAZAWAK17"
                target="_blank"
                rel="noopener noreferrer"
                whileHover={{ scale: 1.1, y: -2 }}
                className="w-10 h-10 bg-gray-800 hover:bg-primary-600 rounded-full flex items-center justify-center text-gray-300 hover:text-white transition-all duration-300"
              >
                <Github size={20} />
              </motion.a>
              <motion.a
                href="#"
                target="_blank"
                rel="noopener noreferrer"
                whileHover={{ scale: 1.1, y: -2 }}
                className="w-10 h-10 bg-gray-800 hover:bg-primary-600 rounded-full flex items-center justify-center text-gray-300 hover:text-white transition-all duration-300"
              >
                <Linkedin size={20} />
              </motion.a>
            </motion.div>
          </motion.div>

          {/* Profile Image */}
          <motion.div
            initial={{ opacity: 0, x: 50 }}
            animate={inView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.8, delay: 0.4 }}
            className="flex justify-center lg:justify-end"
          >
            <div className="relative">
              <motion.div
                animate={{
                  boxShadow: [
                    "0 0 20px rgba(59, 130, 246, 0.3)",
                    "0 0 40px rgba(59, 130, 246, 0.6)",
                    "0 0 20px rgba(59, 130, 246, 0.3)"
                  ]
                }}
                transition={{
                  duration: 3,
                  repeat: Infinity,
                  ease: "easeInOut"
                }}
                className="w-80 h-80 rounded-full overflow-hidden border-4 border-primary-500/30"
              >
                <img
                  src={aboutImage}
                  alt="Hamza Zawak"
                  className="w-full h-full object-cover"
                  onError={(e) => {
                    console.log('Image failed to load:', e.target.src);
                  }}
                />
              </motion.div>
              
              {/* Floating elements around image */}
              <motion.div
                className="absolute -top-4 -right-4 w-8 h-8 bg-green-400 rounded-full opacity-80"
                animate={{
                  scale: [1, 1.2, 1],
                  opacity: [0.8, 1, 0.8]
                }}
                transition={{
                  duration: 2,
                  repeat: Infinity,
                  ease: "easeInOut"
                }}
              />
              <motion.div
                className="absolute -bottom-4 -left-4 w-6 h-6 bg-blue-400 rounded-full opacity-80"
                animate={{
                  scale: [1, 1.3, 1],
                  opacity: [0.8, 1, 0.8]
                }}
                transition={{
                  duration: 2.5,
                  repeat: Infinity,
                  ease: "easeInOut",
                  delay: 0.5
                }}
              />
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default Home; 